import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Stack,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import {
  Business,
  Add,
  Search,
  Edit,
  Visibility,
  Person,
  PersonAdd,
  Description,
  Favorite,
  Share,
  Delete,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AddPropertyModal from '../modals/AddPropertyModal';
import EditPropertyModal from '../modals/EditPropertyModal';
import EditVendorProfile from '../modals/EditVendorProfile';
import AddInvestorModal from '../modals/AddInvestorModal';
import photosPlaceholderSrc from '../assets/PhotosPlaceholder.png';
import { formatPropertyAddress, propertyImageAlt } from '../utils/propertyDisplay';

export default function VendorLanding() {
  const { userProfile } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const [addPropertyModalOpen, setAddPropertyModalOpen] = useState(false);
  const [editPropertyModalOpen, setEditPropertyModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [addInvestorModalOpen, setAddInvestorModalOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saversModalOpen, setSaversModalOpen] = useState(false);
  const [saversLoading, setSaversLoading] = useState(false);
  const [saversError, setSaversError] = useState(null);
  const [selectedSaversProperty, setSelectedSaversProperty] = useState(null);
  const [propertySavers, setPropertySavers] = useState([]);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareSelectedProperty, setShareSelectedProperty] = useState(null);
  const [shareSelectedInvestorIds, setShareSelectedInvestorIds] = useState([]);
  const [shareInvestorSearch, setShareInvestorSearch] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [shareSending, setShareSending] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteSelectedProperty, setDeleteSelectedProperty] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    bedrooms: 'all',
    propertyType: 'all',
    sort: 'newest',
  });
  const navigate = useNavigate();

  const handleViewSavers = async (property) => {
    try {
      setSelectedSaversProperty(property);
      setSaversModalOpen(true);
      setSaversLoading(true);
      setSaversError(null);
      const resp = await api.properties.getSavedByProperty(property.id);
      setPropertySavers(resp.data || []);
    } catch (err) {
      console.error('Failed to load property savers:', err);
      setSaversError(err?.message || 'Failed to load saves');
      setPropertySavers([]);
    } finally {
      setSaversLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch properties for this vendor
      const propertiesResponse = await api.properties.getByVendor(userProfile?.uid || 'vendor_001');
      console.log('[VendorLanding] fetched properties:', propertiesResponse.data.length, 'for uid', userProfile?.uid);
      setProperties(propertiesResponse.data);

      // Fetch investors that work with this vendor (simulate relationships)
      const investorsResponse = await api.users.getAll();
      // Filter to get only investors, and show first 3 as "your" investors
      const investorUsers = investorsResponse.data.filter(user => user.userType === 'investor');
      setInvestors(investorUsers.slice(0, 3));

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.uid]);

  useEffect(() => {
    if (!userProfile?.uid) {
      return;
    }
    fetchData();
  }, [userProfile?.uid, fetchData]);

  // Support deep-linking into sections, e.g. /vendor#properties
  useEffect(() => {
    if (location.hash === '#properties') {
      // Defer to ensure layout/content is mounted before scrolling.
      const t = setTimeout(() => {
        document.getElementById('properties')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  // Apply filters whenever properties or filters change
  useEffect(() => {
    let filtered = [...properties];

    // Search filter
    if (filters.search) {
      const q = filters.search.toLowerCase();
      filtered = filtered.filter((property) => {
        const address = formatPropertyAddress(property, '').toLowerCase();
        return address.includes(q) || property.description?.toLowerCase().includes(q);
      });
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(property => normalizeStatus(property.status) === filters.status);
    }

    // Bedrooms filter
    if (filters.bedrooms !== 'all') {
      if (filters.bedrooms === 'studio') {
        filtered = filtered.filter(property => property.bedrooms === 0 || !property.bedrooms);
      } else {
        const bedroomCount = parseInt(filters.bedrooms);
        if (bedroomCount >= 5) {
          filtered = filtered.filter((property) => Number(property.bedrooms) >= 5);
        } else {
          filtered = filtered.filter((property) => Number(property.bedrooms) === bedroomCount);
        }
      }
    }

    // Property type filter
    if (filters.propertyType !== 'all') {
      const normalizeType = (v) => {
        if (!v) return '';
        if (typeof v === 'object') return normalizeType(v.id || v.value || v.name || '');
        const raw = String(v).trim().toLowerCase();
        const cleaned = raw.replace(/_/g, '-').replace(/\s+/g, '-');
        const map = {
          'single-family-home': 'single-family',
          'single-family-house': 'single-family',
          'singlefamily': 'single-family',
          'singlefamilyhome': 'single-family',
          'multi-family-home': 'multi-family',
          'multifamily': 'multi-family',
          'condominium': 'condo',
          'town-home': 'townhouse',
        };
        return map[cleaned] || cleaned;
      };

      const selectedType = normalizeType(filters.propertyType);
      filtered = filtered.filter((property) => normalizeType(property.propertyType) === selectedType);
    }

    // Sorting (applied after filters)
    const parseCreatedAtMs = (p) => {
      const createdAt = p?.createdAt;
      if (!createdAt) return 0;
      if (createdAt?.seconds) return createdAt.seconds * 1000;
      const d = new Date(createdAt);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };
    const parsePrice = (p) => {
      const v = p?.price ?? p?.askingPrice ?? null;
      const n = typeof v === 'string' ? Number(v) : v;
      return Number.isFinite(n) ? n : -Infinity;
    };

    const sort = filters.sort || 'newest';
    filtered.sort((a, b) => {
      if (sort === 'newest') return parseCreatedAtMs(b) - parseCreatedAtMs(a);
      if (sort === 'oldest') return parseCreatedAtMs(a) - parseCreatedAtMs(b);
      if (sort === 'price_high') return parsePrice(b) - parsePrice(a);
      if (sort === 'price_low') return parsePrice(a) - parsePrice(b);
      if (sort === 'views_high') return Number(b?.views || 0) - Number(a?.views || 0);
      if (sort === 'views_low') return Number(a?.views || 0) - Number(b?.views || 0);
      return 0;
    });

    setFilteredProperties(filtered);
  }, [properties, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const PLACEHOLDER_PHOTO_SRC = photosPlaceholderSrc;

  // Helper function to normalize status values
  const normalizeStatus = (status) => {
    if (!status) return 'available';
    const statusMap = {
      'Available': 'available',
      'available': 'available',
      'On-Hold': 'on-hold',
      'on-hold': 'on-hold',
      'First Come First Serve': 'first come first serve',
      'first come first serve': 'first come first serve',
      'Vacant on Lockbox': 'vacant on lockbox',
      'vacant on lockbox': 'vacant on lockbox',
      'Under Contract': 'under contract',
      'under contract': 'under contract',
      'Pending': 'pending',
      'pending': 'pending',
      'Sold': 'sold',
      'sold': 'sold'
    };
    return statusMap[status] || 'available';
  };

  const handleEditProperty = (property) => {
    setSelectedProperty(property);
    setEditPropertyModalOpen(true);
  };

  const handleEditPropertyClose = () => {
    setEditPropertyModalOpen(false);
    setSelectedProperty(null);
    // Refresh properties after edit
    fetchData();
  };

  const handleAddInvestor = (newInvestor) => {
    // Add to investors list (in a real app, this would be an API call)
    setInvestors(prev => [...prev, newInvestor]);
  };

  const handlePropertyAdded = () => {
    // Refresh properties after adding a new one
    fetchData();
  };

  const openShareModal = (property) => {
    setShareSelectedProperty(property);
    setShareSelectedInvestorIds([]);
    setShareInvestorSearch('');
    setShareMessage('');
    setShareError(null);
    setShareSuccess(null);
    setShareModalOpen(true);
  };

  const closeShareModal = () => {
    if (shareSending) return;
    setShareModalOpen(false);
  };

  const filteredInvestorsForShare = investors.filter((inv) => {
    const q = shareInvestorSearch.trim().toLowerCase();
    if (!q) return true;
    const name = (inv.displayName || inv.name || '').toLowerCase();
    const email = (inv.email || '').toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  const toggleShareInvestor = (investorId) => {
    setShareSelectedInvestorIds((prev) => {
      const id = String(investorId);
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const toggleSelectAllShareInvestors = () => {
    const allIds = filteredInvestorsForShare.map((i) => String(i.uid || i.id)).filter(Boolean);
    const allSelected = allIds.length > 0 && allIds.every((id) => shareSelectedInvestorIds.includes(id));
    setShareSelectedInvestorIds(allSelected ? [] : allIds);
  };

  const handleConfirmShare = async () => {
    try {
      if (!shareSelectedProperty?.id) return;
      setShareSending(true);
      setShareError(null);
      setShareSuccess(null);

      const vendorName = userProfile?.displayName || userProfile?.email || null;
      await api.properties.shareToInvestors(shareSelectedProperty.id, shareSelectedInvestorIds, {
        vendorName,
        message: shareMessage?.trim() || null,
      });

      setShareSuccess(`Shared with ${shareSelectedInvestorIds.length} investor${shareSelectedInvestorIds.length === 1 ? '' : 's'}.`);
    } catch (err) {
      console.error('Failed to share property:', err);
      setShareError(err?.message || 'Failed to share property');
    } finally {
      setShareSending(false);
    }
  };

  const openDeleteModal = (property) => {
    setDeleteSelectedProperty(property);
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      if (!deleteSelectedProperty?.id) return;
      setDeleteLoading(true);
      setDeleteError(null);
      await api.properties.deleteCascade(deleteSelectedProperty.id);
      setDeleteModalOpen(false);
      setDeleteSelectedProperty(null);
      await fetchData();
    } catch (err) {
      console.error('Failed to delete property:', err);
      setDeleteError(err?.message || 'Failed to delete property');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Container maxWidth={false} sx={{ py: 6, backgroundColor: '#f8f9fa', px: { xs: 2, md: 6, lg: 8 } }}>
        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Your Investors */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#008080', display: 'flex', alignItems: 'center' }}>
                    <Person sx={{ mr: 1 }} />
                    Your Investors
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonAdd />}
                    onClick={() => setAddInvestorModalOpen(true)}
                    sx={{
                      borderColor: '#008080',
                      color: '#008080',
                      '&:hover': {
                        borderColor: '#005252',
                        backgroundColor: 'rgba(0, 128, 128, 0.04)'
                      }
                    }}
                  >
                    Add Investor
                  </Button>
                </Box>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress sx={{ color: '#008080' }} size={24} />
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {investors.map((investor) => (
                      <Card key={investor.id} variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={investor.profilePhoto}
                            sx={{ width: 40, height: 40, bgcolor: '#008080' }}
                          >
                            {investor.displayName?.charAt(0) || investor.email?.charAt(0) || 'I'}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {investor.displayName || investor.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Investor
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {investor.location || 'Location not specified'}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => window.open(`mailto:${investor.email}`, '_blank')}
                            sx={{
                              borderColor: '#008080',
                              color: '#008080',
                              minWidth: 'auto',
                              px: 2,
                              '&:hover': {
                                borderColor: '#005252',
                                backgroundColor: 'rgba(0, 128, 128, 0.04)'
                              }
                            }}
                          >
                            Contact
                          </Button>
                        </Box>
                      </Card>
                    ))}
                    {investors.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No investors assigned yet
                      </Typography>
                    )}
                  </Stack>
                )}

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      color: '#008080',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 128, 128, 0.04)'
                      }
                    }}
                  >
                    Find More Investors
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Properties Management */}
          <Grid item xs={12} md={8}>
            <Box id="properties" />
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Business sx={{ mr: 1 }} />
                    Your Properties ({filteredProperties.length})
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setAddPropertyModalOpen(true)}
                    sx={{
                      backgroundColor: '#008080',
                      color: 'white',
                      '&:hover': { backgroundColor: '#005252' }
                    }}
                  >
                    Add New Property
                  </Button>
                </Box>

                {/* Filters */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      placeholder="Search properties..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filters.status}
                        label="Status"
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                      >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="available">Available</MenuItem>
                        <MenuItem value="on-hold">On-Hold</MenuItem>
                        <MenuItem value="first come first serve">First Come First Serve</MenuItem>
                        <MenuItem value="vacant on lockbox">Vacant on Lockbox</MenuItem>
                        <MenuItem value="under contract">Under Contract</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="sold">Sold</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Bedrooms</InputLabel>
                      <Select
                        value={filters.bedrooms}
                        label="Bedrooms"
                        onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                      >
                        <MenuItem value="all">All Bedrooms</MenuItem>
                        <MenuItem value="studio">Studio</MenuItem>
                        <MenuItem value="1">1 Bedroom</MenuItem>
                        <MenuItem value="2">2 Bedrooms</MenuItem>
                        <MenuItem value="3">3 Bedrooms</MenuItem>
                        <MenuItem value="4">4 Bedrooms</MenuItem>
                        <MenuItem value="5">5+ Bedrooms</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Property Type</InputLabel>
                      <Select
                        value={filters.propertyType}
                        label="Property Type"
                        onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                      >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="single-family">Single Family</MenuItem>
                        <MenuItem value="multi-family">Multi Family</MenuItem>
                        <MenuItem value="condo">Condo</MenuItem>
                        <MenuItem value="townhouse">Townhouse</MenuItem>
                        <MenuItem value="duplex">Duplex</MenuItem>
                        <MenuItem value="commercial">Commercial</MenuItem>
                        <MenuItem value="land">Land</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sort</InputLabel>
                      <Select
                        value={filters.sort}
                        label="Sort"
                        onChange={(e) => handleFilterChange('sort', e.target.value)}
                      >
                        <MenuItem value="newest">Newest</MenuItem>
                        <MenuItem value="oldest">Oldest</MenuItem>
                        <MenuItem value="price_high">Price (High → Low)</MenuItem>
                        <MenuItem value="price_low">Price (Low → High)</MenuItem>
                        <MenuItem value="views_high">Views (High → Low)</MenuItem>
                        <MenuItem value="views_low">Views (Low → High)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Properties Table */}
                {isMobile ? (
                  <Stack spacing={2}>
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress sx={{ color: '#008080' }} />
                      </Box>
                    ) : filteredProperties.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No properties found matching your filters.
                      </Typography>
                    ) : (
                      filteredProperties.map((property) => {
                        const createdAt =
                          property.createdAt?.seconds
                            ? new Date(property.createdAt.seconds * 1000)
                            : property.createdAt
                            ? new Date(property.createdAt)
                            : null;
                        const days =
                          createdAt && !Number.isNaN(createdAt.getTime())
                            ? Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24))
                            : null;

                        const disclosuresCount = property.disclosures?.length || 0;

                        return (
                          <Card key={property.id} variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={1.25}>
                              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                <Box
                                  sx={{
                                    width: 64,
                                    height: 48,
                                    backgroundColor: 'grey.200',
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                  }}
                                >
                                  {property.images && property.images.length > 0 ? (
                                    <Box
                                      component="img"
                                      src={property.images[0]}
                                      alt={propertyImageAlt(property)}
                                        onError={(e) => {
                                          const img = e.currentTarget;
                                          if (img?.src && img.src.includes('PhotosPlaceholder.png')) return;
                                          img.src = PLACEHOLDER_PHOTO_SRC;
                                        }}
                                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                  ) : (
                                      <Box
                                        component="img"
                                        src={PLACEHOLDER_PHOTO_SRC}
                                        alt="Photos coming soon"
                                        sx={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'white' }}
                                      />
                                  )}
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                                    {formatPropertyAddress(property)}
                                  </Typography>
                                </Box>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleViewSavers(property)}
                                  sx={{
                                    minWidth: 'auto',
                                    px: 1.5,
                                    borderColor: '#FF6B6B',
                                    color: '#FF6B6B',
                                    '&:hover': { borderColor: '#ff3b3b', backgroundColor: 'rgba(255, 107, 107, 0.06)' }
                                  }}
                                >
                                  <Favorite fontSize="small" />
                                </Button>
                              </Box>

                              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                <Chip
                                  label={property.status || 'available'}
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      normalizeStatus(property.status) === 'available' ? '#28a745' :
                                      normalizeStatus(property.status) === 'on-hold' ? '#dc3545' :
                                      normalizeStatus(property.status) === 'first come first serve' ? '#17a2b8' :
                                      normalizeStatus(property.status) === 'vacant on lockbox' ? '#008080' :
                                      normalizeStatus(property.status) === 'under contract' ? '#ffc107' :
                                      normalizeStatus(property.status) === 'pending' ? '#6f42c1' :
                                      normalizeStatus(property.status) === 'sold' ? '#fd7e14' :
                                      '#6c757d',
                                    color: 'white'
                                  }}
                                />
                                <Chip
                                  label={property.visibility || 'public'}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor:
                                      (property.visibility || 'public') === 'public' ? '#28a745' :
                                      (property.visibility || 'public') === 'private' ? '#ffc107' :
                                      '#28a745',
                                    color:
                                      (property.visibility || 'public') === 'public' ? '#28a745' :
                                      (property.visibility || 'public') === 'private' ? '#ffc107' :
                                      '#28a745',
                                  }}
                                />
                                <Chip
                                  label={disclosuresCount > 0 ? `${disclosuresCount} disclosure${disclosuresCount === 1 ? '' : 's'}` : 'No disclosures'}
                                  size="small"
                                  variant="outlined"
                                />
                              </Stack>

                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#008080' }}>
                                    {(() => {
                                      const p =
                                        Number.isFinite(property.price) ? property.price :
                                        Number.isFinite(property.askingPrice) ? property.askingPrice :
                                        null;
                                      return p == null ? 'N/A' : `$${p.toLocaleString()}`;
                                    })()}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {days == null ? 'Date unknown' : `${days} day${days === 1 ? '' : 's'} ago`}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {property.views || 0} views
                                  </Typography>
                                </Box>
                              </Box>

                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  onClick={() => handleEditProperty(property)}
                                  sx={{
                                    borderColor: '#008080',
                                    color: '#008080',
                                    '&:hover': { borderColor: '#005252', backgroundColor: 'rgba(0, 128, 128, 0.04)' }
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  onClick={() => openShareModal(property)}
                                  sx={{
                                    borderColor: '#FFA500',
                                    color: '#FFA500',
                                    '&:hover': { borderColor: '#CC8400', backgroundColor: 'rgba(255, 165, 0, 0.08)' }
                                  }}
                                >
                                  Share
                                </Button>
                              </Stack>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  onClick={() => openDeleteModal(property)}
                                  sx={{
                                    borderColor: '#dc3545',
                                    color: '#dc3545',
                                    '&:hover': { borderColor: '#b02a37', backgroundColor: 'rgba(220, 53, 69, 0.08)' }
                                  }}
                                >
                                  Delete
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  fullWidth
                                  onClick={() => navigate(`/property/${property.id}`)}
                                  sx={{ backgroundColor: '#008080', '&:hover': { backgroundColor: '#005252' } }}
                                >
                                  View
                                </Button>
                              </Stack>
                            </Stack>
                          </Card>
                        );
                      })
                    )}
                  </Stack>
                ) : (
                  <TableContainer component={Paper} sx={{ boxShadow: 2, overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Property</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Disclosures</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Listed</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Saved By</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                              <CircularProgress sx={{ color: '#008080' }} />
                            </TableCell>
                          </TableRow>
                        ) : filteredProperties.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="body1" color="text.secondary">
                                No properties found matching your filters.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredProperties.map((property) => (
                            <TableRow key={property.id} hover sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box
                                    sx={{
                                      width: 60,
                                      height: 40,
                                      backgroundColor: 'grey.200',
                                      borderRadius: 1,
                                      mr: 2,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    {property.images && property.images.length > 0 ? (
                                      <Box
                                        component="img"
                                        src={property.images[0]}
                                        alt={propertyImageAlt(property)}
                                        onError={(e) => {
                                          const img = e.currentTarget;
                                          if (img?.src && img.src.includes('PhotosPlaceholder.png')) return;
                                          img.src = PLACEHOLDER_PHOTO_SRC;
                                        }}
                                        sx={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                      />
                                    ) : (
                                      <Box
                                        component="img"
                                        src={PLACEHOLDER_PHOTO_SRC}
                                        alt="Photos coming soon"
                                        sx={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'white' }}
                                      />
                                    )}
                                  </Box>
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {formatPropertyAddress(property)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Stack spacing={0.5}>
                                  {property.bedrooms && (
                                    <Typography variant="body2">
                                      {property.bedrooms}BR
                                    </Typography>
                                  )}
                                  {property.bathrooms && (
                                    <Typography variant="body2">
                                      {property.bathrooms}BA
                                    </Typography>
                                  )}
                                  {property.squareFootage && (
                                    <Typography variant="body2">
                                      {property.squareFootage.toLocaleString()} sqft
                                    </Typography>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1" sx={{ fontWeight: 500, color: '#008080' }}>
                                  {Number.isFinite(property.price) ? `$${property.price.toLocaleString()}` : 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Stack spacing={1}>
                                  <Chip
                                    label={property.status}
                                    size="small"
                                    sx={{
                                      backgroundColor:
                                        normalizeStatus(property.status) === 'available' ? '#28a745' :
                                        normalizeStatus(property.status) === 'on-hold' ? '#dc3545' :
                                        normalizeStatus(property.status) === 'first come first serve' ? '#17a2b8' :
                                        normalizeStatus(property.status) === 'vacant on lockbox' ? '#008080' :
                                        normalizeStatus(property.status) === 'under contract' ? '#ffc107' :
                                        normalizeStatus(property.status) === 'pending' ? '#6f42c1' :
                                        normalizeStatus(property.status) === 'sold' ? '#fd7e14' :
                                        '#6c757d',
                                      color: 'white'
                                    }}
                                  />
                                  <Chip
                                    label={property.visibility || 'public'}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderColor:
                                        (property.visibility || 'public') === 'public' ? '#28a745' :
                                        (property.visibility || 'public') === 'private' ? '#ffc107' :
                                        '#28a745',
                                      color:
                                        (property.visibility || 'public') === 'public' ? '#28a745' :
                                        (property.visibility || 'public') === 'private' ? '#ffc107' :
                                        '#28a745',
                                      fontSize: '0.7rem',
                                      height: '20px'
                                    }}
                                  />
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {property.disclosures && property.disclosures.length > 0 ? (
                                    <>
                                      <Description sx={{ color: '#008080', fontSize: 18 }} />
                                      <Typography variant="body2" sx={{ color: '#008080' }}>
                                        {property.disclosures.length} file{property.disclosures.length > 1 ? 's' : ''}
                                      </Typography>
                                    </>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      None
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {(() => {
                                    const createdAt =
                                      property.createdAt?.seconds
                                        ? new Date(property.createdAt.seconds * 1000)
                                        : property.createdAt
                                        ? new Date(property.createdAt)
                                        : null;
                                    if (!createdAt || Number.isNaN(createdAt.getTime())) {
                                      return 'Date unknown';
                                    }
                                    const days = Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
                                    return `${days} day${days === 1 ? '' : 's'} ago`;
                                  })()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {property.views || 0} views
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<Favorite />}
                                  onClick={() => handleViewSavers(property)}
                                  sx={{
                                    borderColor: '#FF6B6B',
                                    color: '#FF6B6B',
                                    '&:hover': { borderColor: '#ff3b3b', backgroundColor: 'rgba(255, 107, 107, 0.06)' }
                                  }}
                                >
                                  View
                                </Button>
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                <Stack direction="column" spacing={0.75} alignItems="center">
                                  <Stack direction="row" spacing={1} justifyContent="center">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => handleEditProperty(property)}
                                      sx={{
                                        minWidth: 'auto',
                                        px: 1,
                                        borderColor: '#008080',
                                        color: '#008080',
                                        '&:hover': {
                                          borderColor: '#005252',
                                          backgroundColor: 'rgba(0, 128, 128, 0.04)'
                                        }
                                      }}
                                    >
                                      <Edit fontSize="small" />
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => openShareModal(property)}
                                      sx={{
                                        minWidth: 'auto',
                                        px: 1,
                                        borderColor: '#FFA500',
                                        color: '#FFA500',
                                        '&:hover': {
                                          borderColor: '#CC8400',
                                          backgroundColor: 'rgba(255, 165, 0, 0.08)'
                                        }
                                      }}
                                    >
                                      <Share fontSize="small" />
                                    </Button>
                                  </Stack>
                                  <Stack direction="row" spacing={1} justifyContent="center">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => openDeleteModal(property)}
                                      sx={{
                                        minWidth: 'auto',
                                        px: 1,
                                        borderColor: '#dc3545',
                                        color: '#dc3545',
                                        '&:hover': {
                                          borderColor: '#b02a37',
                                          backgroundColor: 'rgba(220, 53, 69, 0.08)'
                                        }
                                      }}
                                    >
                                      <Delete fontSize="small" />
                                    </Button>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => navigate(`/property/${property.id}`)}
                                      sx={{
                                        minWidth: 'auto',
                                        px: 1,
                                        backgroundColor: '#008080',
                                        '&:hover': { backgroundColor: '#005252' }
                                      }}
                                    >
                                      <Visibility fontSize="small" />
                                    </Button>
                                  </Stack>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <AddPropertyModal
        open={addPropertyModalOpen}
        onClose={() => setAddPropertyModalOpen(false)}
        onPropertyAdded={handlePropertyAdded}
        vendorId={userProfile?.uid}
      />
      <EditPropertyModal
        open={editPropertyModalOpen}
        onClose={handleEditPropertyClose}
        property={selectedProperty}
        vendorId={userProfile?.uid}
      />
      <EditVendorProfile
        open={editProfileModalOpen}
        onClose={() => setEditProfileModalOpen(false)}
      />

      <AddInvestorModal
        open={addInvestorModalOpen}
        onClose={() => setAddInvestorModalOpen(false)}
        onAddInvestor={handleAddInvestor}
      />

      <Dialog
        open={saversModalOpen}
        onClose={() => setSaversModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Saved by investors
          {selectedSaversProperty ? ` - ${formatPropertyAddress(selectedSaversProperty)}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {saversError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {saversError}
            </Typography>
          )}
          {saversLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress sx={{ color: '#008080' }} />
            </Box>
          ) : propertySavers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No investors have saved this property yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {propertySavers.map((s) => (
                <Box key={`${s.propertyId}-${s.investorId}`} sx={{ p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {s.investorName || 'Investor'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {s.investorId}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaversModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Share Property Dialog */}
      <Dialog
        open={shareModalOpen}
        onClose={closeShareModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Share property
          {shareSelectedProperty ? ` - ${formatPropertyAddress(shareSelectedProperty)}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {shareError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {shareError}
            </Typography>
          )}
          {shareSuccess && (
            <Typography sx={{ mb: 2, color: '#008080', fontWeight: 600 }}>
              {shareSuccess}
            </Typography>
          )}

          <TextField
            fullWidth
            size="small"
            label="Search investors"
            value={shareInvestorSearch}
            onChange={(e) => setShareInvestorSearch(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            size="small"
            label="Optional message"
            value={shareMessage}
            onChange={(e) => setShareMessage(e.target.value)}
            sx={{ mb: 2 }}
          />

          {investors.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Your investor list is empty. Add investors first, then you can share properties with them.
            </Typography>
          ) : filteredInvestorsForShare.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No investors match your search.
            </Typography>
          ) : (
            <Box>
              <Button
                variant="text"
                size="small"
                onClick={toggleSelectAllShareInvestors}
                disabled={shareSending}
                sx={{ mb: 1 }}
              >
                {filteredInvestorsForShare
                  .map((i) => String(i.uid || i.id))
                  .filter(Boolean)
                  .every((id) => shareSelectedInvestorIds.includes(id))
                  ? 'Clear selection'
                  : 'Select all'}
              </Button>

              <Stack spacing={1}>
                {filteredInvestorsForShare.map((inv) => {
                  const invId = String(inv.uid || inv.id);
                  const checked = shareSelectedInvestorIds.includes(invId);
                  const primary = inv.displayName || inv.name || inv.email || 'Investor';

                  return (
                    <Box
                      key={invId}
                      onClick={() => toggleShareInvestor(invId)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.25,
                        border: '1px solid #eee',
                        borderRadius: 1,
                        cursor: 'pointer',
                        backgroundColor: checked ? 'rgba(0, 128, 128, 0.06)' : 'transparent',
                        '&:hover': { backgroundColor: checked ? 'rgba(0, 128, 128, 0.08)' : 'rgba(0,0,0,0.02)' }
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '4px',
                          border: `2px solid ${checked ? '#008080' : '#bbb'}`,
                          backgroundColor: checked ? '#008080' : 'transparent',
                          flexShrink: 0
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {primary}
                        </Typography>
                        {inv.email && (
                          <Typography variant="caption" color="text.secondary">
                            {inv.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShareModal} disabled={shareSending}>Close</Button>
          <Button
            variant="contained"
            onClick={handleConfirmShare}
            disabled={shareSending || shareSelectedInvestorIds.length === 0 || !shareSelectedProperty?.id}
            sx={{
              backgroundColor: '#008080',
              '&:hover': { backgroundColor: '#005252' }
            }}
          >
            {shareSending ? 'Sharing...' : `Share (${shareSelectedInvestorIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Property Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Delete property
          {deleteSelectedProperty ? ` - ${formatPropertyAddress(deleteSelectedProperty)}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {deleteError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {deleteError}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will permanently delete the property. This cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Saved/share records will be cleaned up automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteLoading || !deleteSelectedProperty?.id}
            sx={{
              backgroundColor: '#dc3545',
              '&:hover': { backgroundColor: '#b02a37' }
            }}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
